/**
 * 録音画面。固定スクリプトを 1 つずつ読み上げて録音し、品質チェックを通った録音を蓄積する。
 *
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business voice-enrollment Web の録音画面コンポーネント
 * @graph-connects useRecorder [calls] MediaRecorder hook
 * @graph-connects runDefaultQualityCheck [calls] 標準品質チェック
 */

import { useState, type ReactNode } from 'react';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { MicButton } from '../components/MicButton.js';
import { type MessagesCatalog, type ReadingScript } from '../i18n/index.js';
import { type QualityVerdict } from '../lib/audio-quality.js';
import { runDefaultQualityCheck } from '../lib/quality-check.js';
import type { CompletedRecording } from '../types.js';
import { useRecorder, type UseRecorderOptions } from '../hooks/use-recorder.js';

interface RecordingPageProps {
  messages: MessagesCatalog;
  scripts: readonly ReadingScript[];
  onComplete: (recordings: CompletedRecording[]) => void;
  recorderOptions?: UseRecorderOptions;
  /** テスト用 — decode をスキップし固定 verdict を返す */
  qualityCheckImpl?: (blob: Blob) => Promise<QualityVerdict>;
}

/**
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business 録音画面の本体。スクリプトごとに録音 → 品質チェック → 次へ進めるループを管理
 * @graph-connects useRecorder [calls] MediaRecorder hook
 * @graph-connects runDefaultQualityCheck [calls] 標準品質チェック
 */
export function RecordingPage({
  messages,
  scripts,
  onComplete,
  recorderOptions,
  qualityCheckImpl,
}: RecordingPageProps): ReactNode {
  const [scriptIndex, setScriptIndex] = useState(0);
  const [completed, setCompleted] = useState<CompletedRecording[]>([]);
  const [verdict, setVerdict] = useState<QualityVerdict | null>(null);
  const [checking, setChecking] = useState(false);
  const recorder = useRecorder(recorderOptions);
  const currentScript = scripts[scriptIndex];
  const blob = recorder.blob;

  if (!currentScript) {
    return null;
  }

  const handleStop = async (): Promise<void> => {
    await recorder.stop();
  };

  const handleCheck = async (acceptedBlob: Blob): Promise<void> => {
    setChecking(true);
    try {
      const result = await (qualityCheckImpl ?? runDefaultQualityCheck)(acceptedBlob);
      setVerdict(result);
    } finally {
      setChecking(false);
    }
  };

  const handleAccept = (acceptedBlob: Blob): void => {
    const next = [...completed, { scriptId: currentScript.id, blob: acceptedBlob }];
    if (scriptIndex + 1 < scripts.length) {
      setCompleted(next);
      setScriptIndex(scriptIndex + 1);
      setVerdict(null);
      recorder.reset();
    } else {
      onComplete(next);
    }
  };

  const handleRetake = (): void => {
    setVerdict(null);
    recorder.reset();
  };

  const handleMicClick = (): void => {
    if (recorder.status === 'recording') {
      void handleStop();
      return;
    }
    if (recorder.status === 'stopped' && blob && !verdict && !checking) {
      void handleCheck(blob);
      return;
    }
    if (
      recorder.status === 'idle' ||
      recorder.status === 'error' ||
      recorder.status === 'stopped'
    ) {
      void recorder.start();
    }
  };

  const isLast = scriptIndex + 1 === scripts.length;
  const acceptLabel = isLast ? messages.recording.submitLabel : messages.recording.nextLabel;

  const micState =
    recorder.status === 'recording'
      ? 'recording'
      : checking || recorder.status === 'requesting'
        ? 'processing'
        : 'idle';

  const micAriaLabel =
    micState === 'recording'
      ? messages.recording.stopLabel
      : micState === 'processing'
        ? messages.recording.progressLabel
        : messages.recording.startLabel;

  const showCheckOrAccept = recorder.status === 'stopped' && blob;

  return (
    <Card>
      <div className="flex flex-col gap-7">
        <header className="flex items-center justify-between gap-3">
          <h2 className="text-2xl font-semibold tracking-tight text-[var(--color-text-primary)]">
            {messages.recording.title}
          </h2>
          <span className="rounded-[var(--radius-full)] bg-[var(--color-bg-elevated)] px-3 py-1 font-mono text-xs text-[var(--color-text-secondary)] ring-1 ring-[var(--color-border)]">
            {scriptIndex + 1} / {scripts.length}
          </span>
        </header>

        <p className="text-sm leading-relaxed text-[var(--color-text-secondary)]">
          {messages.recording.instruction}
        </p>

        <blockquote className="relative rounded-[var(--radius-xl)] bg-[var(--color-bg-elevated)] p-6 ring-1 ring-[var(--color-border)]">
          <span
            aria-hidden
            className="absolute -left-1 top-4 h-[calc(100%-2rem)] w-1 rounded-full bg-[var(--color-accent)]"
          />
          <p className="text-lg leading-[var(--leading-relaxed)] text-[var(--color-text-primary)]">
            {currentScript.text}
          </p>
        </blockquote>

        <div className="flex flex-col items-center gap-4 py-2">
          <MicButton
            state={micState}
            onClick={handleMicClick}
            ariaLabel={micAriaLabel}
            disabled={Boolean(showCheckOrAccept && verdict)}
          />
          <span className="text-xs font-medium uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
            {micState === 'recording'
              ? messages.recording.progressLabel
              : micState === 'processing'
                ? messages.recording.progressLabel
                : showCheckOrAccept
                  ? verdict
                    ? verdict.acceptable
                      ? messages.recording.readyHint
                      : messages.recording.retryHint
                    : messages.recording.checkHint
                  : messages.recording.recordHint}
          </span>
        </div>

        {recorder.error ? (
          <p
            role="alert"
            className="rounded-[var(--radius-md)] bg-[var(--color-danger-soft)] px-4 py-3 text-sm text-[var(--color-danger)]"
          >
            {recorder.error}
          </p>
        ) : null}

        {verdict && !verdict.acceptable ? (
          <div
            role="alert"
            className="rounded-[var(--radius-lg)] bg-[var(--color-warning-soft)] p-4 ring-1 ring-[var(--color-warning)]/40"
          >
            <p className="text-sm font-semibold text-[var(--color-warning)]">
              {messages.recording.qualityIssue}
            </p>
            <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
              {messages.recording.qualityHint}
            </p>
          </div>
        ) : null}

        {showCheckOrAccept && verdict ? (
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button variant="ghost" onClick={handleRetake}>
              {messages.recording.retakeLabel}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                handleAccept(blob);
              }}
              disabled={!verdict.acceptable}
              rightIcon={
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              }
            >
              {acceptLabel}
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
